import { usePortalContext } from '@/contexts/PortalProvider';
import { UserCategoryTypeBC } from '@/data/models/user-category-type.model';
import type { UserPermission } from '@/data/models/permission.model';
import { getPortalName } from './usePortalName';

/**
 * High-level identity of the current portal — matches the classification
 * exposed by `PortalProvider`/`usePortalContext`:
 *   `'f1'`            — non-portal (operator/admin) URL.
 *   `'member-portal'` — recognized portal URL and the user's category is Passenger.
 *   `'client-portal'` — recognized portal URL and the user has client-portal permissions.
 *
 * Returns `'f1'` as a safe fallback when the user identity isn't loaded yet.
 */
export type PortalLabel = 'f1' | 'member-portal' | 'client-portal';

const CLIENT_TRIPLEG_PERMISSION: UserPermission = {
  resource: 'tripleg',
  action: 'readbasic',
  scope: 'client',
};
const CLIENT_USERS_PERMISSION: UserPermission = {
  resource: 'users',
  action: 'createbasic',
  scope: 'client',
};

function userHasPermission(user: any, permission: UserPermission): boolean {
  return !!user?.access?.permissions?.some(
    (p: UserPermission) =>
      p.resource === permission.resource &&
      p.action === permission.action &&
      p.scope === permission.scope,
  );
}

function classify(input: {
  isPortal: boolean;
  isMemberPortal: boolean;
  isClientPortal: boolean;
}): PortalLabel {
  if (!input.isPortal) return 'f1';
  if (input.isClientPortal) return 'client-portal';
  if (input.isMemberPortal) return 'member-portal';
  return 'f1';
}

/** React hook — reads identity from `usePortalContext`. */
export function usePortalLabel(): PortalLabel {
  const { isPortal, isMemberPortal, isClientPortal } = usePortalContext();
  return classify({ isPortal, isMemberPortal, isClientPortal });
}

/**
 * Late-bound accessor for the logged-in user. Set once at app bootstrap (see
 * `App.tsx`) so this module doesn't have to import the redux store directly —
 * which would create a `store.ts` → `reservation.slice.ts` → `usePortalLabel.ts` →
 * `store.ts` cycle and break TDZ on `userReducer`.
 *
 * Until the accessor is wired up, `getPortalLabel()` returns `'f1'`.
 */
type LoggedInUserAccessor = () => unknown;
let getLoggedInUser: LoggedInUserAccessor = () => null;

export function configurePortalLabelUserAccessor(
  accessor: LoggedInUserAccessor,
): void {
  getLoggedInUser = accessor;
}

/**
 * Non-React equivalent of `usePortalLabel` for services and thunks. Reads the
 * logged-in user via the late-bound accessor and the URL slug directly, then
 * applies the same classification `PortalProvider` uses.
 */
export function getPortalLabel(): PortalLabel {
  const isPortal = getPortalName() != null;
  const user = getLoggedInUser() as
    | { userCategoryTypeBC?: unknown }
    | null
    | undefined;
  const isMemberPortal =
    user?.userCategoryTypeBC === UserCategoryTypeBC.Passenger;
  const hasClientTripleg = userHasPermission(user, CLIENT_TRIPLEG_PERMISSION);
  const hasClientUsers = userHasPermission(user, CLIENT_USERS_PERMISSION);
  const isClientPortalUser = hasClientTripleg && !hasClientUsers;
  const isClientPortalAdminUser = hasClientTripleg && hasClientUsers;
  const isClientPortal = isClientPortalUser || isClientPortalAdminUser;
  return classify({ isPortal, isMemberPortal, isClientPortal });
}
